import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { ScrollArea } from '@/components/ui/scroll-area';
import Navigation from '@/components/navigation';
import MessageCarousel from '@/components/MessageCarousel';
import { useToast } from '@/hooks/use-toast';
import SwygWaveMark from '@/components/SwygWaveMark';

interface DrinkLog {
  id: number;
  amount: number;
  time: Date;
}

const HomeNew = () => {
  const [waterIntake, setWaterIntake] = useState(1.5);
  const [timesDrank, setTimesDrank] = useState(5);
  const [timesSkipped, setTimesSkipped] = useState(0);
  const [showDrinkDialog, setShowDrinkDialog] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showRipple, setShowRipple] = useState(false);
  const [drinkLogs, setDrinkLogs] = useState<DrinkLog[]>([
    { id: 1, amount: 300, time: new Date(Date.now() - 3600000 * 4) },
    { id: 2, amount: 200, time: new Date(Date.now() - 3600000 * 3) },
    { id: 3, amount: 250, time: new Date(Date.now() - 3600000 * 2) },
    { id: 4, amount: 300, time: new Date(Date.now() - 3600000) },
    { id: 5, amount: 450, time: new Date(Date.now() - 1800000) },
  ]);
  const goal = 2.0;
  const { toast } = useToast();

  const triggerHaptic = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate([50, 30, 50]);
    }
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const logDrink = (amount: number) => {
    const newIntake = Math.min(waterIntake + amount / 1000, goal);

    // Trigger haptic feedback
    triggerHaptic();

    // Trigger animations
    setIsAnimating(true);
    setShowRipple(true);

    setTimeout(() => setIsAnimating(false), 500);
    setTimeout(() => setShowRipple(false), 600);

    // Add to drink logs
    setDrinkLogs((prev) => [...prev, { id: Date.now(), amount, time: new Date() }]);

    setWaterIntake(newIntake);
    setTimesDrank((prev) => prev + 1);
    setShowDrinkDialog(false);
    setShowCustomInput(false);
    setCustomAmount('');

    if (newIntake >= goal) {
      // Extra haptic for goal achievement
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100, 50, 100]);
      }
      toast({
        title: 'Goal achieved! 🎉',
        description: "You've reached your daily hydration goal!",
      });
    } else {
      toast({
        title: 'Great job! 💧',
        description: `You've logged ${amount}ml. Keep it up!`,
      });
    }
  };

  const handleCustomSubmit = () => {
    const amount = parseInt(customAmount);
    if (amount > 0 && amount <= 2000) {
      logDrink(amount);
    }
  };

  const percentage = (waterIntake / goal) * 100;

  const drinkOptions = [
    { label: '100ml', value: 100 },
    { label: '200ml', value: 200 },
    { label: '300ml', value: 300 },
  ];

  return (
    <div className="min-h-screen pb-24 transition-colors duration-700 bg-hydro-100">
      <div className="max-w-md mx-auto px-6 py-8 space-y-6">
        {/* 1. LOGO */}
        <div className="flex justify-center">
          <SwygWaveMark size="lg" />
        </div>

        {/* 2. MESSAGE/AD LAYER - Auto-rotating carousel */}
        <MessageCarousel />

        {/* 3. THE CARD - Swipable Carousel */}
        <Carousel className="w-full">
          <CarouselContent>
            {/* First Card - Daily Progress */}
            <CarouselItem>
              <Card className="p-8 shadow-lg border-none overflow-hidden relative bg-gradient-to-br from-lilac-300 via-lilac-200 to-purple-200">
                {/* Progress Circle */}
                <div className="flex justify-center mb-8">
                  <div className={`relative w-48 h-48 ${isAnimating ? 'animate-drink-pulse' : ''}`}>
                    {showRipple && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-48 h-48 rounded-full bg-purple-400/30 animate-ripple" />
                      </div>
                    )}
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="96"
                        cy="96"
                        r="88"
                        stroke="rgba(255, 255, 255, 0.3)"
                        strokeWidth="8"
                        fill="none"
                      />
                      <circle
                        cx="96"
                        cy="96"
                        r="88"
                        stroke="hsl(var(--purple-500))"
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 88}`}
                        strokeDashoffset={`${2 * Math.PI * 88 * (1 - percentage / 100)}`}
                        className="transition-all duration-500"
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span
                        className={`text-5xl font-bold text-deep-800 ${isAnimating ? 'animate-number-pop' : ''}`}
                      >
                        {waterIntake.toFixed(1)} L
                      </span>
                      <span className="text-deep-500 text-sm">Of {goal}L</span>
                      <span className="text-deep-500 text-sm">Goal</span>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex gap-4 justify-center mb-8">
                  <div className="bg-white/50 backdrop-blur-sm rounded-2xl px-8 py-4 text-center border border-lilac-300">
                    <div className="text-3xl font-bold text-purple-600 mb-1">{timesDrank}</div>
                    <div className="text-xs text-deep-600 uppercase tracking-wide">
                      Times
                      <br />
                      Drank
                    </div>
                  </div>
                  <div className="bg-white/50 backdrop-blur-sm rounded-2xl px-8 py-4 text-center border border-lilac-300">
                    <div className="text-3xl font-bold text-purple-600 mb-1">{timesSkipped}</div>
                    <div className="text-xs text-deep-600 uppercase tracking-wide">
                      Times
                      <br />
                      Skipped
                    </div>
                  </div>
                </div>

                {/* Log Button */}
                <Button
                  onClick={() => setShowDrinkDialog(true)}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full py-6 text-lg font-semibold tracking-wide transition-all shadow-lg"
                >
                  LOG A DRINK
                </Button>
              </Card>
            </CarouselItem>

            {/* Second Card - Drink Logs */}
            <CarouselItem>
              <Card className="p-6 shadow-lg border-none overflow-hidden relative bg-gradient-to-br from-lilac-300 via-lilac-200 to-purple-200 h-[460px] flex flex-col">
                <h3 className="text-center text-deep-700 font-semibold mb-3">Today's Drinks</h3>

                {/* Scrollable Logs */}
                <ScrollArea className="flex-1 -mx-2 px-2">
                  <div className="space-y-2">
                    {drinkLogs.length === 0 ? (
                      <p className="text-center text-deep-500 py-4">No drinks logged yet</p>
                    ) : (
                      [...drinkLogs].reverse().map((log) => (
                        <div
                          key={log.id}
                          className="bg-white/50 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center justify-between border border-lilac-300"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-400/30 flex items-center justify-center">
                              <span className="text-sm">💧</span>
                            </div>
                            <span className="font-medium text-deep-700">{log.amount}ml</span>
                          </div>
                          <span className="text-sm text-deep-500">{formatTime(log.time)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </Card>
            </CarouselItem>
          </CarouselContent>
        </Carousel>
      </div>

      {/* Drink Amount Dialog */}
      <Dialog
        open={showDrinkDialog}
        onOpenChange={(open) => {
          setShowDrinkDialog(open);
          if (!open) {
            setShowCustomInput(false);
            setCustomAmount('');
          }
        }}
      >
        <DialogContent className="bg-gradient-to-br from-lilac-100 via-lilac-50 to-purple-50 border-lilac-300 rounded-3xl max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-deep-800 text-xl font-semibold pr-8">
              How much did you drink?
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 pt-2">
            {!showCustomInput ? (
              <>
                {drinkOptions.map((option) => (
                  <Button
                    key={option.value}
                    onClick={() => logDrink(option.value)}
                    variant="outline"
                    className="w-full bg-white/60 hover:bg-purple-100 border-lilac-300 text-deep-700 rounded-xl py-5 text-base font-medium transition-all"
                  >
                    {option.label}
                  </Button>
                ))}
                <Button
                  onClick={() => setShowCustomInput(true)}
                  variant="outline"
                  className="w-full bg-purple-100 hover:bg-purple-200 border-purple-300 text-purple-700 rounded-xl py-5 text-base font-medium transition-all"
                >
                  Custom
                </Button>
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="bg-white/60 border-lilac-300 rounded-xl text-center text-lg"
                    min="1"
                    max="2000"
                  />
                  <span className="text-deep-600 font-medium">ml</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowCustomInput(false)}
                    variant="outline"
                    className="flex-1 bg-white/60 border-lilac-300 text-deep-600 rounded-xl"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleCustomSubmit}
                    disabled={!customAmount || parseInt(customAmount) <= 0}
                    className="flex-1 bg-purple-500 hover:bg-purple-600 text-white rounded-xl"
                  >
                    Log
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 4. NAVIGATION */}
      <Navigation />
    </div>
  );
};

export default HomeNew;
